import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FraudShieldBadge } from './FraudShieldBadge';
import {
  Shield, AlertTriangle, ShieldAlert, ShieldX, Clock, CheckCircle,
  XCircle, Eye, FileText, User, Truck, Loader2, RefreshCw,
  Flag, Ban, Search
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  open: 'text-yellow-400 bg-yellow-500/20',
  investigating: 'text-blue-400 bg-blue-500/20',
  resolved: 'text-green-400 bg-green-500/20',
  dismissed: 'text-gray-400 bg-gray-500/20'
};

const reportTypeLabels = {
  fake_broker: 'Fake Broker',
  fake_carrier: 'Fake Carrier',
  suspicious_load: 'Suspicious Load',
  double_brokering: 'Double Brokering',
  payment_concern: 'Payment Concern',
  identity_concern: 'Identity Concern',
  scam_attempt: 'Scam Attempt',
  other: 'Other'
};

const FraudShieldAdmin = () => {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionUserId, setActionUserId] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [statusFilter, typeFilter]);

  const loadData = async () => {
    try {
      const [statsRes, reportsRes] = await Promise.all([
        api.get('/fraud-shield/stats'),
        api.get('/fraud-shield/reports', {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            report_type: typeFilter !== 'all' ? typeFilter : undefined
          }
        })
      ]);
      
      setStats(statsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Failed to load fraud shield data:', error);
      toast.error('Failed to load fraud data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReport = async (reportId, newStatus) => {
    try {
      await api.put(`/fraud-shield/reports/${reportId}`, { status: newStatus });
      toast.success('Report updated');
      loadData();
      setDetailsOpen(false);
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleAddNotes = async (reportId, notes) => {
    try {
      await api.put(`/fraud-shield/reports/${reportId}`, { admin_notes: notes });
      toast.success('Notes saved');
      loadData();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleUserAction = async () => {
    if (!actionUserId || !actionReason) return;
    
    setProcessing(true);
    try {
      if (actionType === 'flag') {
        await api.post(`/fraud-shield/users/${actionUserId}/flag?reason=${encodeURIComponent(actionReason)}`);
        toast.success('User flagged');
      } else if (actionType === 'suspend') {
        await api.post(`/fraud-shield/users/${actionUserId}/suspend?reason=${encodeURIComponent(actionReason)}`);
        toast.success('User suspended');
      } else if (actionType === 'unflag') {
        await api.post(`/fraud-shield/users/${actionUserId}/unflag`);
        toast.success('User unflagged');
      } else if (actionType === 'unsuspend') {
        await api.post(`/fraud-shield/users/${actionUserId}/unsuspend`);
        toast.success('User unsuspended');
      }
      
      setActionDialogOpen(false);
      setActionReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to perform action');
    } finally {
      setProcessing(false);
    }
  };

  const openUserAction = (type, userId) => {
    setActionType(type);
    setActionUserId(userId);
    setActionDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#4ADE80] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#4ADE80]" />
          <h2 className="text-xl font-bold text-white">Fraud Shield</h2>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-yellow-400 text-xs mb-1">
                <AlertTriangle className="w-4 h-4" />
                Open Reports
              </div>
              <div className="text-2xl font-bold text-white">{stats.open_reports}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-400 text-xs mb-1">
                <Search className="w-4 h-4" />
                Investigating
              </div>
              <div className="text-2xl font-bold text-white">{stats.investigating_reports}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-orange-400 text-xs mb-1">
                <ShieldAlert className="w-4 h-4" />
                Flagged Users
              </div>
              <div className="text-2xl font-bold text-white">{stats.flagged_users}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-400 text-xs mb-1">
                <ShieldX className="w-4 h-4" />
                Suspended
              </div>
              <div className="text-2xl font-bold text-white">{stats.suspended_users}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Clock className="w-4 h-4" />
                This Week
              </div>
              <div className="text-2xl font-bold text-white">{stats.reports_this_week}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-[#12151a] border-[#1a1d24]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="investigating">Investigating</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 bg-[#12151a] border-[#1a1d24]">
            <SelectValue placeholder="Report Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(reportTypeLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reports List */}
      <Card className="bg-[#12151a] border-[#1a1d24]">
        <CardHeader>
          <CardTitle className="text-white">Fraud Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No reports found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-[#0a0c10] border border-[#1a1d24] rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${statusColors[report.status]}`}>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {reportTypeLabels[report.report_type]}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDate(report.created_at)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-300 mb-3 line-clamp-2">
                    {report.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    {report.related_user_name && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {report.related_user_name}
                      </div>
                    )}
                    {report.related_load_info && (
                      <div className="flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {report.related_load_info}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Flag className="w-3 h-3" />
                      by {report.reporter_name}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedReport(report);
                        setDetailsOpen(true);
                      }}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Review
                    </Button>
                    {report.related_user_id && report.status !== 'dismissed' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-400 border-orange-500/30"
                          onClick={() => openUserAction('flag', report.related_user_id)}
                        >
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          Flag User
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-400 border-red-500/30"
                          onClick={() => openUserAction('suspend', report.related_user_id)}
                        >
                          <Ban className="w-3 h-3 mr-1" />
                          Suspend
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Report Details</DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Report Type</Label>
                  <p className="text-white">{reportTypeLabels[selectedReport.report_type]}</p>
                </div>
                <div>
                  <Label className="text-gray-400">Status</Label>
                  <p className={`${statusColors[selectedReport.status].split(' ')[0]}`}>
                    {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-400">Description</Label>
                <p className="text-white bg-[#0a0c10] rounded-lg p-3 mt-1">
                  {selectedReport.description}
                </p>
              </div>
              
              {selectedReport.related_user_name && (
                <div>
                  <Label className="text-gray-400">Related User</Label>
                  <p className="text-white">{selectedReport.related_user_name}</p>
                </div>
              )}
              
              {selectedReport.related_load_info && (
                <div>
                  <Label className="text-gray-400">Related Load</Label>
                  <p className="text-white">{selectedReport.related_load_info}</p>
                </div>
              )}
              
              {selectedReport.evidence_file_url && (
                <div>
                  <Label className="text-gray-400">Evidence</Label>
                  <a 
                    href={selectedReport.evidence_file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#4ADE80] hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    View Evidence File
                  </a>
                </div>
              )}
              
              <div>
                <Label className="text-gray-400">Admin Notes</Label>
                <Textarea
                  defaultValue={selectedReport.admin_notes || ''}
                  placeholder="Add internal notes..."
                  className="bg-[#0a0c10] border-[#1a1d24] mt-1"
                  onBlur={(e) => handleAddNotes(selectedReport.id, e.target.value)}
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Update Status</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-blue-400"
                    onClick={() => handleUpdateReport(selectedReport.id, 'investigating')}
                  >
                    Investigating
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-400"
                    onClick={() => handleUpdateReport(selectedReport.id, 'resolved')}
                  >
                    Resolved
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-gray-400"
                    onClick={() => handleUpdateReport(selectedReport.id, 'dismissed')}
                  >
                    Dismissed
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="bg-[#12151a] border-[#1a1d24]">
          <DialogHeader>
            <DialogTitle className="text-white capitalize">
              {actionType} User
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {(actionType === 'flag' || actionType === 'suspend') && (
              <div>
                <Label className="text-white">Reason (required)</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Provide a detailed reason for this action..."
                  className="bg-[#0a0c10] border-[#1a1d24] mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 10 characters</p>
              </div>
            )}
            
            {(actionType === 'unflag' || actionType === 'unsuspend') && (
              <p className="text-gray-400">
                Are you sure you want to {actionType} this user?
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUserAction}
              disabled={processing || ((actionType === 'flag' || actionType === 'suspend') && actionReason.length < 10)}
              className={
                actionType === 'suspend' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-[#4ADE80] text-black hover:bg-[#22C55E]'
              }
            >
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                `${actionType?.charAt(0).toUpperCase()}${actionType?.slice(1)}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FraudShieldAdmin;
